import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { ArrowRight, ShoppingBasket } from 'lucide-react';
import { CartProvider, useCart } from '@/contexts/CartContext';
import { SiteHeader } from '@/components/SiteHeader';
import { Hero } from '@/components/Hero';
import { Marquee } from '@/components/Marquee';
import { MenuSection } from '@/components/MenuSection';
import { HowItWorks } from '@/components/HowItWorks';
import { AboutCta } from '@/components/AboutCta';
import { SiteFooter } from '@/components/SiteFooter';
import { CartDrawer } from '@/components/CartDrawer';
import { CheckoutDialog } from '@/components/CheckoutDialog';
import { formatKES } from '@/lib/format';

/** Floating "view cart" pill — appears once the basket has items. */
function FloatingCartBar({ onOpenCart }: { onOpenCart: () => void }) {
  const { count, total, isCartOpen } = useCart();

  if (count === 0 || isCartOpen) return null;

  return (
    <div className="fixed inset-x-4 bottom-4 z-30 sm:hidden">
      <button
        type="button"
        onClick={onOpenCart}
        className="flex w-full items-center justify-between gap-3 rounded-full bg-secondary px-5 py-4 text-secondary-foreground shadow-2xl shadow-black/25 transition-transform active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-2.5 text-sm font-bold">
          <span className="relative">
            <ShoppingBasket className="size-5" />
            <span className="absolute -right-2 -top-2 flex min-w-4 items-center justify-center rounded-full bg-marigold px-1 text-[10px] font-black text-accent-foreground">
              {count}
            </span>
          </span>
          View cart
        </span>
        <span className="flex items-center gap-2 text-sm font-black">
          {formatKES(total)}
          <ArrowRight className="size-4" />
        </span>
      </button>
    </div>
  );
}

function Page() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { setCartOpen } = useCart();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <Hero />
        <Marquee />
        <MenuSection />
        <HowItWorks />
        <AboutCta />
      </main>
      <SiteFooter />
      <FloatingCartBar onOpenCart={() => setCartOpen(true)} />
      <CartDrawer onCheckout={() => setCheckoutOpen(true)} />
      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </div>
  );
}

const Index = () => {
  useSeoMeta({
    title: 'MLO — Kenyan Meals Delivered Hot | Breakfast, Lunch & Dinner',
    description:
      'Order authentic Kenyan meals — ugali, nyama choma, pilau, mandazi and more — delivered hot across Nairobi in 30–45 minutes. Pay instantly with M-Pesa or Tando.',
    ogTitle: 'MLO — Kenyan Meals Delivered Hot',
    ogDescription:
      'Real Kenyan food, hot to your door. Breakfast, lunch & dinner in Nairobi. Pay with M-Pesa or Tando.',
    ogType: 'website',
  });

  return (
    <CartProvider>
      <Page />
    </CartProvider>
  );
};

export default Index;
